import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Button,
  Badge,
  Icon,
  Divider,
  useToast,
  Tooltip,
  Circle,
  Input,
  FormControl,
  FormLabel,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Code,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import {
  FaCopy,
  FaCheck,
  FaSun,
  FaCoins,
  FaHome,
  FaArrowRight,
  FaBook,
  FaPalette,
  FaSquare,
  FaCircle,
  FaGraduationCap,
  FaLightbulb,
  FaEye,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import { usePageTitle } from "../../hooks/useDocumentTitle";

// =============================================================================
// V3 COLOR PALETTE
// =============================================================================
const V3_COLORS = {
  solar: {
    primary: "#d4a843",
    hover: "#c49a3d",
    name: "Harvest Gold",
  },
  finance: {
    primary: "#d4826a",
    hover: "#c67259",
    name: "Warm Coral",
  },
  property: {
    primary: "#00b5ad",
    hover: "#009e97",
    name: "Ocean Teal",
  },
};

// Color swatch component with copy functionality
const ColorSwatch: React.FC<{
  color: string;
  name: string;
  description?: string;
  size?: "sm" | "md" | "lg";
}> = ({ color, name, description, size = "md" }) => {
  const [copied, setCopied] = useState(false);
  const toast = useToast();

  const sizes = {
    sm: { box: "60px", text: "xs" },
    md: { box: "80px", text: "sm" },
    lg: { box: "100px", text: "md" },
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(color);
    setCopied(true);
    toast({
      title: "Copied!",
      description: `${color} copied to clipboard`,
      status: "success",
      duration: 2000,
      isClosable: true,
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const isLight = (hex: string) => {
    const c = hex.substring(1);
    const rgb = parseInt(c, 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = (rgb >> 0) & 0xff;
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luma > 128;
  };

  const textColor = color.startsWith("#") && isLight(color) ? "gray.800" : "white";

  return (
    <Tooltip label="Click to copy" placement="top">
      <VStack
        spacing={2}
        cursor="pointer"
        onClick={handleCopy}
        transition="all 0.2s"
        _hover={{ transform: "scale(1.05)" }}
      >
        <Box
          w={sizes[size].box}
          h={sizes[size].box}
          bg={color}
          borderRadius="xl"
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxShadow="lg"
          border="2px solid"
          borderColor="gray.200"
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%)",
          }}
        >
          <Icon
            as={copied ? FaCheck : FaCopy}
            color={textColor}
            boxSize={4}
            opacity={0.8}
          />
        </Box>
        <VStack spacing={0}>
          <Text fontSize={sizes[size].text} fontWeight="600" color="gray.800">
            {name}
          </Text>
          <Code fontSize="xs" bg="gray.100" color="gray.600" px={2} borderRadius="md">
            {color}
          </Code>
          {description && (
            <Text fontSize="xs" color="gray.500" textAlign="center" maxW="100px">
              {description}
            </Text>
          )}
        </VStack>
      </VStack>
    </Tooltip>
  );
};

// Section header component
const SectionHeader: React.FC<{
  icon: React.ElementType;
  title: string;
  subtitle?: string;
  color?: string;
}> = ({ icon, title, subtitle, color = "#d4a843" }) => (
  <HStack spacing={4} mb={8}>
    <Circle size="50px" bg={color} boxShadow={`0 4px 20px ${color}40`}>
      <Icon as={icon} boxSize={6} color="white" />
    </Circle>
    <VStack align="start" spacing={0}>
      <Heading size="lg" color="gray.800">
        {title}
      </Heading>
      {subtitle && (
        <Text color="gray.500" fontSize="sm">
          {subtitle}
        </Text>
      )}
    </VStack>
  </HStack>
);

// Light card component
const LightCard: React.FC<{ children: React.ReactNode; p?: number }> = ({
  children,
  p = 8,
}) => (
  <Box
    bg="white"
    borderRadius="2xl"
    border="1px solid"
    borderColor="gray.200"
    p={p}
    boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
  >
    {children}
  </Box>
);

// Archetype fit rating component
const ArchetypeFitRating: React.FC<{
  rating: "strong" | "moderate" | "weak";
  label: string;
}> = ({ rating, label }) => {
  const colors = {
    strong: { bg: "green.50", border: "green.200", icon: FaCheckCircle, iconColor: "#30D158", text: "Strong Fit" },
    moderate: { bg: "yellow.50", border: "yellow.200", icon: FaExclamationTriangle, iconColor: "#FF9F0A", text: "Moderate Fit" },
    weak: { bg: "red.50", border: "red.200", icon: FaExclamationTriangle, iconColor: "#FF3B30", text: "Weak Fit" },
  };
  const config = colors[rating];

  return (
    <HStack
      p={3}
      bg={config.bg}
      border="1px solid"
      borderColor={config.border}
      borderRadius="lg"
      spacing={3}
    >
      <Icon as={config.icon} color={config.iconColor} boxSize={5} />
      <VStack align="start" spacing={0}>
        <Text fontSize="sm" fontWeight="600" color="gray.700">
          {config.text}
        </Text>
        <Text fontSize="xs" color="gray.600">
          {label}
        </Text>
      </VStack>
    </HStack>
  );
};

const BrandStyleGuideV3: React.FC = () => {
  usePageTitle("Brand Style Guide v3");
  const toast = useToast();

  // Primary brand colors
  const primaryColors = [
    { color: "#FFFFFF", name: "White", description: "Page backgrounds" },
    { color: "#001f3f", name: "Deep Navy", description: "Hero sections" },
    { color: "#101c47", name: "Navy", description: "Primary brand" },
  ];

  // V3 Highlight colors
  const highlightColors = [
    { color: V3_COLORS.solar.primary, name: "Harvest Gold", description: "Solar accent" },
    { color: V3_COLORS.solar.hover, name: "Gold Hover", description: "Hover state" },
    { color: V3_COLORS.finance.primary, name: "Warm Coral", description: "Finance accent" },
    { color: V3_COLORS.finance.hover, name: "Coral Hover", description: "Hover state" },
    { color: V3_COLORS.property.primary, name: "Ocean Teal", description: "Property accent" },
    { color: V3_COLORS.property.hover, name: "Teal Hover", description: "Hover state" },
    { color: "#F97316", name: "Warm Ember", description: "Urgency accent" },
  ];

  // Status colors
  const statusColors = [
    { color: "#30D158", name: "Success", description: "Confirmations" },
    { color: "#FF9F0A", name: "Warning", description: "Caution" },
    { color: "#FF3B30", name: "Error", description: "Errors" },
    { color: "#007AFF", name: "Info", description: "Information" },
  ];

  return (
    <>

      {/* Hero Section */}
      <Box
        position="relative"
        py={20}
        overflow="hidden"
        backgroundImage="url('https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment="fixed"
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: "rgba(0, 31, 63, 0.92)",
        }}
        _after={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 30% 20%, rgba(212, 168, 67, 0.15) 0%, transparent 40%), radial-gradient(circle at 70% 80%, rgba(0, 181, 173, 0.1) 0%, transparent 40%)`,
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center">
            <Badge
              bg={`linear-gradient(135deg, ${V3_COLORS.solar.primary} 0%, ${V3_COLORS.finance.primary} 50%, ${V3_COLORS.property.primary} 100%)`}
              color="white"
              px={4}
              py={2}
              borderRadius="full"
              fontSize="sm"
              textTransform="uppercase"
              letterSpacing="wider"
              boxShadow="0 4px 15px rgba(212, 168, 67, 0.4)"
            >
              Brand Guidelines v3.0 — Warm Palette
            </Badge>
            <Heading
              size="3xl"
              bgGradient="linear(to-r, white, gray.300)"
              bgClip="text"
              fontWeight="black"
            >
              One Group Brand System
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="2xl">
              A warm, approachable visual identity built on the{" "}
              <Text as="span" color={V3_COLORS.solar.primary} fontWeight="600">
                Sage Archetype
              </Text>{" "}
              with{" "}
              <Text as="span" color={V3_COLORS.finance.primary} fontWeight="600">
                Caregiver
              </Text>{" "}
              warmth.
            </Text>

            {/* Division color preview */}
            <HStack spacing={8} pt={8}>
              <VStack>
                <Circle size="60px" bg={V3_COLORS.solar.primary} boxShadow={`0 4px 20px ${V3_COLORS.solar.primary}60`}>
                  <Icon as={FaSun} boxSize={7} color="white" />
                </Circle>
                <Text color="gray.400" fontSize="sm">Solar</Text>
              </VStack>
              <VStack>
                <Circle size="60px" bg={V3_COLORS.finance.primary} boxShadow={`0 4px 20px ${V3_COLORS.finance.primary}60`}>
                  <Icon as={FaCoins} boxSize={7} color="white" />
                </Circle>
                <Text color="gray.400" fontSize="sm">Finance</Text>
              </VStack>
              <VStack>
                <Circle size="60px" bg={V3_COLORS.property.primary} boxShadow={`0 4px 20px ${V3_COLORS.property.primary}60`}>
                  <Icon as={FaHome} boxSize={7} color="white" />
                </Circle>
                <Text color="gray.400" fontSize="sm">Property</Text>
              </VStack>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Main content */}
      <Container maxW="container.xl" py={12}>
        <Tabs variant="soft-rounded" colorScheme="orange">
          <TabList
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            p={2}
            borderRadius="2xl"
            flexWrap="wrap"
            justifyContent="center"
            boxShadow="sm"
          >
            <Tab color="gray.500" _selected={{ color: "white", bg: V3_COLORS.solar.primary }}>
              <Icon as={FaPalette} mr={2} /> Colors
            </Tab>
            <Tab color="gray.500" _selected={{ color: "white", bg: V3_COLORS.solar.primary }}>
              <Icon as={FaGraduationCap} mr={2} /> Archetype Fit
            </Tab>
            <Tab color="gray.500" _selected={{ color: "white", bg: V3_COLORS.solar.primary }}>
              <Icon as={FaSquare} mr={2} /> Components
            </Tab>
            <Tab color="gray.500" _selected={{ color: "white", bg: V3_COLORS.solar.primary }}>
              <Icon as={FaSun} mr={2} /> Solar
            </Tab>
            <Tab color="gray.500" _selected={{ color: "white", bg: V3_COLORS.finance.primary }}>
              <Icon as={FaCoins} mr={2} /> Finance
            </Tab>
            <Tab color="gray.500" _selected={{ color: "white", bg: V3_COLORS.property.primary }}>
              <Icon as={FaHome} mr={2} /> Property
            </Tab>
          </TabList>

          <TabPanels mt={8}>
            {/* Colors Panel */}
            <TabPanel px={0}>
              <VStack spacing={12} align="stretch">
                {/* Primary Colors */}
                <LightCard>
                  <SectionHeader
                    icon={FaCircle}
                    title="Foundation Colors"
                    subtitle="The core palette that establishes trust and authority"
                    color={V3_COLORS.solar.primary}
                  />
                  <SimpleGrid columns={{ base: 3, md: 3 }} spacing={8}>
                    {primaryColors.map((c, idx) => (
                      <ColorSwatch key={idx} {...c} size="lg" />
                    ))}
                  </SimpleGrid>
                </LightCard>

                {/* V3 Highlight Colors */}
                <LightCard>
                  <SectionHeader
                    icon={FaLightbulb}
                    title="V3 Division Highlights"
                    subtitle="Warm, approachable accents for each vertical"
                    color={V3_COLORS.finance.primary}
                  />
                  <SimpleGrid columns={{ base: 3, md: 7 }} spacing={6}>
                    {highlightColors.map((c, idx) => (
                      <ColorSwatch key={idx} {...c} />
                    ))}
                  </SimpleGrid>

                  <Divider my={8} borderColor="gray.200" />

                  {/* CTA Hierarchy */}
                  <Heading size="md" color="gray.800" mb={4}>
                    CTA Hierarchy
                  </Heading>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <Box p={4} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200">
                      <Text color={V3_COLORS.solar.hover} fontWeight="600" mb={2}>
                        Primary CTA (Solar)
                      </Text>
                      <Button
                        bg={V3_COLORS.solar.primary}
                        color="white"
                        size="lg"
                        w="full"
                        _hover={{ bg: V3_COLORS.solar.hover }}
                        rightIcon={<FaArrowRight />}
                      >
                        Get a Free Quote
                      </Button>
                    </Box>
                    <Box p={4} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200">
                      <Text color={V3_COLORS.finance.hover} fontWeight="600" mb={2}>
                        Finance CTA
                      </Text>
                      <Button
                        bg={V3_COLORS.finance.primary}
                        color="white"
                        size="lg"
                        w="full"
                        _hover={{ bg: V3_COLORS.finance.hover }}
                        rightIcon={<FaArrowRight />}
                      >
                        Book Consultation
                      </Button>
                    </Box>
                    <Box p={4} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200">
                      <Text color={V3_COLORS.property.hover} fontWeight="600" mb={2}>
                        Property CTA
                      </Text>
                      <Button
                        bg={V3_COLORS.property.primary}
                        color="white"
                        size="lg"
                        w="full"
                        _hover={{ bg: V3_COLORS.property.hover }}
                        rightIcon={<FaArrowRight />}
                      >
                        Start Your Journey
                      </Button>
                    </Box>
                  </SimpleGrid>
                </LightCard>

                {/* Division Colors with Commentary */}
                <LightCard>
                  <SectionHeader
                    icon={FaHome}
                    title="Division Colors"
                    subtitle="Consistent identification across service verticals"
                    color={V3_COLORS.property.primary}
                  />
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    {/* Solar */}
                    <Box
                      p={6}
                      borderRadius="xl"
                      border={`3px solid ${V3_COLORS.solar.primary}`}
                      bg={`linear-gradient(135deg, rgba(212, 168, 67, 0.05) 0%, white 100%)`}
                    >
                      <HStack mb={4}>
                        <Icon as={FaSun} boxSize={8} color={V3_COLORS.solar.primary} />
                        <VStack align="start" spacing={0}>
                          <Text color="gray.800" fontWeight="bold">
                            Solar Division
                          </Text>
                          <Code fontSize="xs" bg="transparent" color={V3_COLORS.solar.hover}>
                            #d4a843
                          </Code>
                        </VStack>
                      </HStack>
                      <Text fontSize="sm" color="gray.600">
                        <Text as="span" fontWeight="600">Harvest Gold</Text> — warmth, energy, sunlight.
                        A natural metaphor for solar power and the rewards of wise investment.
                      </Text>
                    </Box>

                    {/* Finance */}
                    <Box
                      p={6}
                      borderRadius="xl"
                      border={`3px solid ${V3_COLORS.finance.primary}`}
                      bg={`linear-gradient(135deg, rgba(212, 130, 106, 0.05) 0%, white 100%)`}
                    >
                      <HStack mb={4}>
                        <Icon as={FaCoins} boxSize={8} color={V3_COLORS.finance.primary} />
                        <VStack align="start" spacing={0}>
                          <Text color="gray.800" fontWeight="bold">
                            Finance Division
                          </Text>
                          <Code fontSize="xs" bg="transparent" color={V3_COLORS.finance.hover}>
                            #d4826a
                          </Code>
                        </VStack>
                      </HStack>
                      <Text fontSize="sm" color="gray.600">
                        <Text as="span" fontWeight="600">Warm Coral</Text> — approachable, human, caring.
                        Breaks from cold "institutional blue" to signal personal service.
                      </Text>
                    </Box>

                    {/* Property */}
                    <Box
                      p={6}
                      borderRadius="xl"
                      border={`3px solid ${V3_COLORS.property.primary}`}
                      bg={`linear-gradient(135deg, rgba(0, 181, 173, 0.05) 0%, white 100%)`}
                    >
                      <HStack mb={4}>
                        <Icon as={FaHome} boxSize={8} color={V3_COLORS.property.primary} />
                        <VStack align="start" spacing={0}>
                          <Text color="gray.800" fontWeight="bold">
                            Property Division
                          </Text>
                          <Code fontSize="xs" bg="transparent" color={V3_COLORS.property.hover}>
                            #00b5ad
                          </Code>
                        </VStack>
                      </HStack>
                      <Text fontSize="sm" color="gray.600">
                        <Text as="span" fontWeight="600">Ocean Teal</Text> — growth, stability, long-term vision.
                        Blue-green conveys both trust and prosperity.
                      </Text>
                    </Box>
                  </SimpleGrid>
                </LightCard>

                {/* Status Colors */}
                <LightCard>
                  <SectionHeader
                    icon={FaEye}
                    title="Status Colors"
                    subtitle="System feedback and state indicators"
                    color="#30D158"
                  />
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
                    {statusColors.map((c, idx) => (
                      <ColorSwatch key={idx} {...c} />
                    ))}
                  </SimpleGrid>
                </LightCard>

                {/* Quick Reference */}
                <LightCard>
                  <SectionHeader
                    icon={FaLightbulb}
                    title="Quick Reference"
                    subtitle="Copy-paste ready values"
                    color="#30D158"
                  />
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    {[
                      { label: "White (Page BG)", value: "#FFFFFF" },
                      { label: "Deep Navy (Hero)", value: "#001f3f" },
                      { label: "Navy Primary", value: "#101c47" },
                      { label: "Solar Gold", value: "#d4a843" },
                      { label: "Gold Hover", value: "#c49a3d" },
                      { label: "Finance Coral", value: "#d4826a" },
                      { label: "Coral Hover", value: "#c67259" },
                      { label: "Property Teal", value: "#00b5ad" },
                      { label: "Teal Hover", value: "#009e97" },
                      { label: "Success Green", value: "#30D158" },
                      { label: "Warning Orange", value: "#FF9F0A" },
                      { label: "Error Red", value: "#FF3B30" },
                    ].map((item) => (
                      <HStack
                        key={item.label}
                        p={3}
                        bg="gray.50"
                        borderRadius="lg"
                        justify="space-between"
                        cursor="pointer"
                        border="1px solid"
                        borderColor="gray.200"
                        _hover={{ bg: "gray.100" }}
                        onClick={() => {
                          navigator.clipboard.writeText(item.value);
                          toast({
                            title: "Copied!",
                            description: `${item.value}`,
                            status: "success",
                            duration: 1500,
                          });
                        }}
                      >
                        <HStack>
                          <Box
                            w="20px"
                            h="20px"
                            bg={item.value}
                            borderRadius="md"
                            border="1px solid"
                            borderColor="gray.300"
                          />
                          <Text color="gray.700" fontSize="sm">
                            {item.label}
                          </Text>
                        </HStack>
                        <Code fontSize="xs" bg="gray.100" color="gray.600" px={2}>
                          {item.value}
                        </Code>
                      </HStack>
                    ))}
                  </SimpleGrid>
                </LightCard>
              </VStack>
            </TabPanel>

            {/* Archetype Fit Panel */}
            <TabPanel px={0}>
              <VStack spacing={12} align="stretch">
                {/* Sage Archetype Overview */}
                <LightCard>
                  <SectionHeader
                    icon={FaGraduationCap}
                    title="Sage Archetype Analysis"
                    subtitle="Evaluating color choices against brand personality"
                    color={V3_COLORS.solar.primary}
                  />

                  <Alert status="info" mb={8} borderRadius="xl" bg="blue.50" border="1px solid" borderColor="blue.200">
                    <AlertIcon color="#007AFF" />
                    <Box>
                      <Text color="gray.700" fontWeight="600">The Sage Archetype</Text>
                      <Text color="gray.600" fontSize="sm">
                        Seeks truth and wisdom. Values knowledge, expertise, and clarity.
                        Communicates with authority and calm confidence. The secondary Caregiver
                        archetype adds warmth and genuine concern for client outcomes.
                      </Text>
                    </Box>
                  </Alert>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                    {/* Solar Analysis */}
                    <Box
                      p={6}
                      borderRadius="xl"
                      border={`2px solid ${V3_COLORS.solar.primary}`}
                      bg="white"
                    >
                      <HStack mb={4}>
                        <Circle size="50px" bg={V3_COLORS.solar.primary}>
                          <Icon as={FaSun} boxSize={6} color="white" />
                        </Circle>
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="bold" color="gray.800">Solar Division</Text>
                          <Code bg="transparent" color={V3_COLORS.solar.hover}>#d4a843</Code>
                        </VStack>
                      </HStack>

                      <ArchetypeFitRating rating="strong" label="Sage Archetype Alignment" />

                      <VStack align="start" spacing={3} mt={4}>
                        <Box>
                          <Text fontWeight="600" color="gray.700" fontSize="sm">Division Fit</Text>
                          <Text color="gray.600" fontSize="sm">
                            <Icon as={FaCheckCircle} color="#30D158" mr={1} />
                            Gold is a natural metaphor for sunlight and energy harvest.
                          </Text>
                        </Box>
                        <Box>
                          <Text fontWeight="600" color="gray.700" fontSize="sm">Sage Alignment</Text>
                          <Text color="gray.600" fontSize="sm">
                            <Icon as={FaCheckCircle} color="#30D158" mr={1} />
                            Represents "illumination" — the Sage's gift of knowledge bringing light to decisions.
                          </Text>
                        </Box>
                        <Box>
                          <Text fontWeight="600" color="gray.700" fontSize="sm">Psychological Signal</Text>
                          <Text color="gray.600" fontSize="sm">
                            Warmth, optimism, value creation. Evokes the reward of wise choices.
                          </Text>
                        </Box>
                      </VStack>

                      <Badge mt={4} bg="green.100" color="green.700" px={3} py={1} borderRadius="full">
                        Recommended
                      </Badge>
                    </Box>

                    {/* Finance Analysis */}
                    <Box
                      p={6}
                      borderRadius="xl"
                      border={`2px solid ${V3_COLORS.finance.primary}`}
                      bg="white"
                    >
                      <HStack mb={4}>
                        <Circle size="50px" bg={V3_COLORS.finance.primary}>
                          <Icon as={FaCoins} boxSize={6} color="white" />
                        </Circle>
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="bold" color="gray.800">Finance Division</Text>
                          <Code bg="transparent" color={V3_COLORS.finance.hover}>#d4826a</Code>
                        </VStack>
                      </HStack>

                      <ArchetypeFitRating rating="moderate" label="Sage Archetype Alignment" />

                      <VStack align="start" spacing={3} mt={4}>
                        <Box>
                          <Text fontWeight="600" color="gray.700" fontSize="sm">Division Fit</Text>
                          <Text color="gray.600" fontSize="sm">
                            <Icon as={FaExclamationTriangle} color="#FF9F0A" mr={1} />
                            Breaks from traditional finance blue. Warm, but unconventional for the sector.
                          </Text>
                        </Box>
                        <Box>
                          <Text fontWeight="600" color="gray.700" fontSize="sm">Sage Alignment</Text>
                          <Text color="gray.600" fontSize="sm">
                            <Icon as={FaCheckCircle} color="#30D158" mr={1} />
                            Aligns with Caregiver secondary — warmth, personal care, human connection.
                          </Text>
                        </Box>
                        <Box>
                          <Text fontWeight="600" color="gray.700" fontSize="sm">Considerations</Text>
                          <Text color="gray.600" fontSize="sm">
                            <Icon as={FaExclamationTriangle} color="#FF9F0A" mr={1} />
                            May not signal "institutional authority" as strongly as blue tones would.
                          </Text>
                        </Box>
                        <Box>
                          <Text fontWeight="600" color="gray.700" fontSize="sm">Strategic Advantage</Text>
                          <Text color="gray.600" fontSize="sm">
                            <Icon as={FaCheckCircle} color="#30D158" mr={1} />
                            Differentiates from cold bank branding. Signals "we're on your side."
                          </Text>
                        </Box>
                      </VStack>

                      <Badge mt={4} bg="yellow.100" color="yellow.700" px={3} py={1} borderRadius="full">
                        Consider Context
                      </Badge>
                    </Box>

                    {/* Property Analysis */}
                    <Box
                      p={6}
                      borderRadius="xl"
                      border={`2px solid ${V3_COLORS.property.primary}`}
                      bg="white"
                    >
                      <HStack mb={4}>
                        <Circle size="50px" bg={V3_COLORS.property.primary}>
                          <Icon as={FaHome} boxSize={6} color="white" />
                        </Circle>
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="bold" color="gray.800">Property Division</Text>
                          <Code bg="transparent" color={V3_COLORS.property.hover}>#00b5ad</Code>
                        </VStack>
                      </HStack>

                      <ArchetypeFitRating rating="strong" label="Sage Archetype Alignment" />

                      <VStack align="start" spacing={3} mt={4}>
                        <Box>
                          <Text fontWeight="600" color="gray.700" fontSize="sm">Division Fit</Text>
                          <Text color="gray.600" fontSize="sm">
                            <Icon as={FaCheckCircle} color="#30D158" mr={1} />
                            Teal combines blue (trust) with green (growth) — perfect for property investment.
                          </Text>
                        </Box>
                        <Box>
                          <Text fontWeight="600" color="gray.700" fontSize="sm">Sage Alignment</Text>
                          <Text color="gray.600" fontSize="sm">
                            <Icon as={FaCheckCircle} color="#30D158" mr={1} />
                            Conveys stability, measured progress, and long-term thinking — core Sage values.
                          </Text>
                        </Box>
                        <Box>
                          <Text fontWeight="600" color="gray.700" fontSize="sm">Psychological Signal</Text>
                          <Text color="gray.600" fontSize="sm">
                            Building, prosperity, balanced growth. Reassuring without being conservative.
                          </Text>
                        </Box>
                      </VStack>

                      <Badge mt={4} bg="green.100" color="green.700" px={3} py={1} borderRadius="full">
                        Recommended
                      </Badge>
                    </Box>
                  </SimpleGrid>
                </LightCard>

                {/* Summary Table */}
                <LightCard>
                  <SectionHeader
                    icon={FaBook}
                    title="Color-Archetype Summary"
                    subtitle="Quick reference for brand decisions"
                    color={V3_COLORS.property.primary}
                  />

                  <Box overflowX="auto">
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th color="gray.500" borderColor="gray.200">Division</Th>
                          <Th color="gray.500" borderColor="gray.200">Color</Th>
                          <Th color="gray.500" borderColor="gray.200">Sage Fit</Th>
                          <Th color="gray.500" borderColor="gray.200">Caregiver Fit</Th>
                          <Th color="gray.500" borderColor="gray.200">Industry Fit</Th>
                          <Th color="gray.500" borderColor="gray.200">Notes</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        <Tr>
                          <Td fontWeight="600" color="gray.700" borderColor="gray.200">
                            <HStack>
                              <Circle size="24px" bg={V3_COLORS.solar.primary}>
                                <Icon as={FaSun} boxSize={3} color="white" />
                              </Circle>
                              <Text>Solar</Text>
                            </HStack>
                          </Td>
                          <Td borderColor="gray.200">
                            <Code bg="transparent" color={V3_COLORS.solar.primary}>#d4a843</Code>
                          </Td>
                          <Td borderColor="gray.200">
                            <Badge bg="green.100" color="green.700">Strong</Badge>
                          </Td>
                          <Td borderColor="gray.200">
                            <Badge bg="green.100" color="green.700">Strong</Badge>
                          </Td>
                          <Td borderColor="gray.200">
                            <Badge bg="green.100" color="green.700">Strong</Badge>
                          </Td>
                          <Td color="gray.600" fontSize="sm" borderColor="gray.200">
                            Natural solar metaphor. Keep.
                          </Td>
                        </Tr>
                        <Tr>
                          <Td fontWeight="600" color="gray.700" borderColor="gray.200">
                            <HStack>
                              <Circle size="24px" bg={V3_COLORS.finance.primary}>
                                <Icon as={FaCoins} boxSize={3} color="white" />
                              </Circle>
                              <Text>Finance</Text>
                            </HStack>
                          </Td>
                          <Td borderColor="gray.200">
                            <Code bg="transparent" color={V3_COLORS.finance.primary}>#d4826a</Code>
                          </Td>
                          <Td borderColor="gray.200">
                            <Badge bg="yellow.100" color="yellow.700">Moderate</Badge>
                          </Td>
                          <Td borderColor="gray.200">
                            <Badge bg="green.100" color="green.700">Strong</Badge>
                          </Td>
                          <Td borderColor="gray.200">
                            <Badge bg="yellow.100" color="yellow.700">Moderate</Badge>
                          </Td>
                          <Td color="gray.600" fontSize="sm" borderColor="gray.200">
                            Warm but unconventional. Differentiates from banks.
                          </Td>
                        </Tr>
                        <Tr>
                          <Td fontWeight="600" color="gray.700" borderColor="gray.200">
                            <HStack>
                              <Circle size="24px" bg={V3_COLORS.property.primary}>
                                <Icon as={FaHome} boxSize={3} color="white" />
                              </Circle>
                              <Text>Property</Text>
                            </HStack>
                          </Td>
                          <Td borderColor="gray.200">
                            <Code bg="transparent" color={V3_COLORS.property.primary}>#00b5ad</Code>
                          </Td>
                          <Td borderColor="gray.200">
                            <Badge bg="green.100" color="green.700">Strong</Badge>
                          </Td>
                          <Td borderColor="gray.200">
                            <Badge bg="green.100" color="green.700">Strong</Badge>
                          </Td>
                          <Td borderColor="gray.200">
                            <Badge bg="green.100" color="green.700">Strong</Badge>
                          </Td>
                          <Td color="gray.600" fontSize="sm" borderColor="gray.200">
                            Trust + growth. Ideal for property.
                          </Td>
                        </Tr>
                      </Tbody>
                    </Table>
                  </Box>

                  <Alert status="info" mt={8} borderRadius="xl" bg="blue.50" border="1px solid" borderColor="blue.200">
                    <AlertIcon color="#007AFF" />
                    <Box>
                      <Text color="gray.700" fontWeight="600">Finance Color Consideration</Text>
                      <Text color="gray.600" fontSize="sm">
                        The coral (#d4826a) is a deliberate departure from finance-industry blue. It signals
                        "we're not like the banks" — more personal, more human. This aligns with the Caregiver
                        secondary archetype but may require messaging that reinforces expertise to balance the warmth.
                        Consider whether your avatar client responds more to institutional authority (blue) or
                        personal connection (coral).
                      </Text>
                    </Box>
                  </Alert>
                </LightCard>
              </VStack>
            </TabPanel>

            {/* Components Panel */}
            <TabPanel px={0}>
              <VStack spacing={12} align="stretch">
                {/* Buttons */}
                <LightCard>
                  <SectionHeader
                    icon={FaSquare}
                    title="Buttons"
                    subtitle="Interactive elements with V3 colors"
                    color={V3_COLORS.solar.primary}
                  />
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                    {/* Solar Buttons */}
                    <Box>
                      <Text color="gray.500" fontSize="sm" mb={4}>SOLAR BUTTONS</Text>
                      <VStack spacing={4} align="stretch">
                        <Button
                          bg={V3_COLORS.solar.primary}
                          color="white"
                          size="lg"
                          _hover={{ bg: V3_COLORS.solar.hover }}
                          rightIcon={<FaArrowRight />}
                        >
                          Get a Free Quote
                        </Button>
                        <Button
                          variant="outline"
                          borderColor={V3_COLORS.solar.primary}
                          color={V3_COLORS.solar.hover}
                          size="lg"
                          _hover={{ bg: `rgba(212, 168, 67, 0.1)` }}
                        >
                          Calculate Savings
                        </Button>
                      </VStack>
                    </Box>

                    {/* Finance Buttons */}
                    <Box>
                      <Text color="gray.500" fontSize="sm" mb={4}>FINANCE BUTTONS</Text>
                      <VStack spacing={4} align="stretch">
                        <Button
                          bg={V3_COLORS.finance.primary}
                          color="white"
                          size="lg"
                          _hover={{ bg: V3_COLORS.finance.hover }}
                          rightIcon={<FaArrowRight />}
                        >
                          Book Consultation
                        </Button>
                        <Button
                          variant="outline"
                          borderColor={V3_COLORS.finance.primary}
                          color={V3_COLORS.finance.hover}
                          size="lg"
                          _hover={{ bg: `rgba(212, 130, 106, 0.1)` }}
                        >
                          Check Eligibility
                        </Button>
                      </VStack>
                    </Box>

                    {/* Property Buttons */}
                    <Box>
                      <Text color="gray.500" fontSize="sm" mb={4}>PROPERTY BUTTONS</Text>
                      <VStack spacing={4} align="stretch">
                        <Button
                          bg={V3_COLORS.property.primary}
                          color="white"
                          size="lg"
                          _hover={{ bg: V3_COLORS.property.hover }}
                          rightIcon={<FaArrowRight />}
                        >
                          Start Your Journey
                        </Button>
                        <Button
                          variant="outline"
                          borderColor={V3_COLORS.property.primary}
                          color={V3_COLORS.property.hover}
                          size="lg"
                          _hover={{ bg: `rgba(0, 181, 173, 0.1)` }}
                        >
                          View Properties
                        </Button>
                      </VStack>
                    </Box>
                  </SimpleGrid>
                </LightCard>

                {/* Badges */}
                <LightCard>
                  <SectionHeader
                    icon={FaCircle}
                    title="Badges & Tags"
                    subtitle="Labels and status indicators"
                    color={V3_COLORS.property.primary}
                  />
                  <Wrap spacing={4}>
                    <WrapItem>
                      <Badge bg={V3_COLORS.solar.primary} color="white" px={3} py={1} borderRadius="full">
                        Solar
                      </Badge>
                    </WrapItem>
                    <WrapItem>
                      <Badge bg={V3_COLORS.finance.primary} color="white" px={3} py={1} borderRadius="full">
                        Finance
                      </Badge>
                    </WrapItem>
                    <WrapItem>
                      <Badge bg={V3_COLORS.property.primary} color="white" px={3} py={1} borderRadius="full">
                        Property
                      </Badge>
                    </WrapItem>
                    <WrapItem>
                      <Badge bg="#30D158" color="white" px={3} py={1} borderRadius="full">
                        Active
                      </Badge>
                    </WrapItem>
                    <WrapItem>
                      <Badge bg="#FF9F0A" color="white" px={3} py={1} borderRadius="full">
                        Pending
                      </Badge>
                    </WrapItem>
                    <WrapItem>
                      <Badge variant="outline" borderColor={V3_COLORS.solar.primary} color={V3_COLORS.solar.hover} px={3} py={1} borderRadius="full">
                        Solar
                      </Badge>
                    </WrapItem>
                    <WrapItem>
                      <Badge variant="outline" borderColor={V3_COLORS.finance.primary} color={V3_COLORS.finance.hover} px={3} py={1} borderRadius="full">
                        Finance
                      </Badge>
                    </WrapItem>
                    <WrapItem>
                      <Badge variant="outline" borderColor={V3_COLORS.property.primary} color={V3_COLORS.property.hover} px={3} py={1} borderRadius="full">
                        Property
                      </Badge>
                    </WrapItem>
                  </Wrap>
                </LightCard>

                {/* Cards */}
                <LightCard>
                  <SectionHeader
                    icon={FaSquare}
                    title="Service Cards"
                    subtitle="Content containers for each division"
                    color={V3_COLORS.finance.primary}
                  />
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    {/* Solar Card */}
                    <Box
                      p={6}
                      bg="white"
                      borderRadius="xl"
                      border={`2px solid ${V3_COLORS.solar.primary}`}
                      _hover={{ transform: "translateY(-4px)", shadow: "xl" }}
                      transition="all 0.3s"
                    >
                      <Icon as={FaSun} boxSize={10} color={V3_COLORS.solar.primary} mb={4} />
                      <Heading size="md" color="gray.800" mb={2}>
                        Solar Energy
                      </Heading>
                      <Text color="gray.600" fontSize="sm" mb={4}>
                        Residential & commercial solar solutions
                      </Text>
                      <Button bg={V3_COLORS.solar.primary} color="white" size="sm" _hover={{ bg: V3_COLORS.solar.hover }}>
                        Explore
                      </Button>
                    </Box>

                    {/* Finance Card */}
                    <Box
                      p={6}
                      bg="white"
                      borderRadius="xl"
                      border={`2px solid ${V3_COLORS.finance.primary}`}
                      _hover={{ transform: "translateY(-4px)", shadow: "xl" }}
                      transition="all 0.3s"
                    >
                      <Icon as={FaCoins} boxSize={10} color={V3_COLORS.finance.primary} mb={4} />
                      <Heading size="md" color="gray.800" mb={2}>
                        Finance
                      </Heading>
                      <Text color="gray.600" fontSize="sm" mb={4}>
                        Home loans & wealth management
                      </Text>
                      <Button bg={V3_COLORS.finance.primary} color="white" size="sm" _hover={{ bg: V3_COLORS.finance.hover }}>
                        Explore
                      </Button>
                    </Box>

                    {/* Property Card */}
                    <Box
                      p={6}
                      bg="white"
                      borderRadius="xl"
                      border={`2px solid ${V3_COLORS.property.primary}`}
                      _hover={{ transform: "translateY(-4px)", shadow: "xl" }}
                      transition="all 0.3s"
                    >
                      <Icon as={FaHome} boxSize={10} color={V3_COLORS.property.primary} mb={4} />
                      <Heading size="md" color="gray.800" mb={2}>
                        Property
                      </Heading>
                      <Text color="gray.600" fontSize="sm" mb={4}>
                        Investment property advisory
                      </Text>
                      <Button bg={V3_COLORS.property.primary} color="white" size="sm" _hover={{ bg: V3_COLORS.property.hover }}>
                        Explore
                      </Button>
                    </Box>
                  </SimpleGrid>
                </LightCard>

                {/* Form Elements */}
                <LightCard>
                  <SectionHeader
                    icon={FaSquare}
                    title="Form Elements"
                    subtitle="Inputs with division-specific focus states"
                    color={V3_COLORS.property.primary}
                  />
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                    <FormControl>
                      <FormLabel color="gray.700">Solar Enquiry</FormLabel>
                      <Input
                        placeholder="Your property address"
                        bg="white"
                        border="1px solid"
                        borderColor="gray.300"
                        _focus={{
                          borderColor: V3_COLORS.solar.primary,
                          boxShadow: `0 0 0 1px ${V3_COLORS.solar.primary}`,
                        }}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel color="gray.700">Finance Application</FormLabel>
                      <Input
                        placeholder="Loan amount"
                        bg="white"
                        border="1px solid"
                        borderColor="gray.300"
                        _focus={{
                          borderColor: V3_COLORS.finance.primary,
                          boxShadow: `0 0 0 1px ${V3_COLORS.finance.primary}`,
                        }}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel color="gray.700">Property Search</FormLabel>
                      <Input
                        placeholder="Suburb or postcode"
                        bg="white"
                        border="1px solid"
                        borderColor="gray.300"
                        _focus={{
                          borderColor: V3_COLORS.property.primary,
                          boxShadow: `0 0 0 1px ${V3_COLORS.property.primary}`,
                        }}
                      />
                    </FormControl>
                  </SimpleGrid>
                </LightCard>
              </VStack>
            </TabPanel>

            {/* Solar Home Page Preview */}
            <TabPanel px={0}>
              <VStack spacing={0} align="stretch">
                {/* Solar Hero */}
                <Box
                  position="relative"
                  py={20}
                  overflow="hidden"
                  backgroundImage="url('https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1920&q=80')"
                  backgroundSize="cover"
                  backgroundPosition="center"
                  _before={{
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bg: "rgba(0, 31, 63, 0.88)",
                  }}
                >
                  <Container maxW="container.xl" position="relative" zIndex={1}>
                    <VStack spacing={6} textAlign="center">
                      <Circle size="80px" bg={V3_COLORS.solar.primary} boxShadow={`0 8px 30px rgba(212, 168, 67, 0.4)`}>
                        <Icon as={FaSun} boxSize={10} color="white" />
                      </Circle>
                      <Badge bg={V3_COLORS.solar.primary} color="white" px={4} py={2} borderRadius="full" fontSize="sm">
                        Solar Division
                      </Badge>
                      <Heading size="3xl" color="white" fontWeight="black">
                        Power Your Future with Solar
                      </Heading>
                      <Text fontSize="xl" color="gray.300" maxW="2xl">
                        Australian-owned solar solutions for homes, businesses, and investment properties
                      </Text>
                      <HStack spacing={4} pt={4}>
                        <Button bg={V3_COLORS.solar.primary} color="white" size="lg" _hover={{ bg: V3_COLORS.solar.hover }} rightIcon={<FaArrowRight />}>
                          Get a Free Quote
                        </Button>
                        <Button variant="outline" borderColor="white" color="white" size="lg" _hover={{ bg: "whiteAlpha.200" }}>
                          Calculate Savings
                        </Button>
                      </HStack>
                    </VStack>
                  </Container>
                </Box>

                {/* Solar Stats */}
                <Box bg="white" py={12} borderBottom="1px solid" borderColor="gray.200">
                  <Container maxW="container.xl">
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
                      {[
                        { value: "2,500+", label: "Systems Installed" },
                        { value: "50MW+", label: "Energy Generated" },
                        { value: "$15M+", label: "Customer Savings" },
                        { value: "15,000t", label: "CO2 Offset" },
                      ].map((stat, idx) => (
                        <VStack key={idx}>
                          <Text fontSize="4xl" fontWeight="bold" color={V3_COLORS.solar.primary}>
                            {stat.value}
                          </Text>
                          <Text color="gray.600">{stat.label}</Text>
                        </VStack>
                      ))}
                    </SimpleGrid>
                  </Container>
                </Box>

                {/* Solar Services */}
                <Box bg="gray.50" py={16}>
                  <Container maxW="container.xl">
                    <VStack spacing={4} mb={12} textAlign="center">
                      <Text color={V3_COLORS.solar.hover} fontWeight="600" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
                        Our Solutions
                      </Text>
                      <Heading size="xl" color="gray.800">
                        Solar for Every Need
                      </Heading>
                    </VStack>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                      {[
                        { title: "Residential Solar", desc: "Custom-designed systems for homes of all sizes", icon: FaHome },
                        { title: "Commercial Solar", desc: "Large-scale solutions for business energy needs", icon: FaLightbulb },
                        { title: "Investment Properties", desc: "Add value and appeal to your rental portfolio", icon: FaCoins },
                      ].map((service, idx) => (
                        <Box
                          key={idx}
                          p={8}
                          bg="white"
                          borderRadius="2xl"
                          border={`2px solid ${V3_COLORS.solar.primary}`}
                          textAlign="center"
                          _hover={{ transform: "translateY(-4px)", shadow: "xl" }}
                          transition="all 0.3s"
                        >
                          <Circle size="60px" bg={`rgba(212, 168, 67, 0.1)`} mx="auto" mb={4}>
                            <Icon as={service.icon} boxSize={6} color={V3_COLORS.solar.primary} />
                          </Circle>
                          <Heading size="md" color="gray.800" mb={2}>
                            {service.title}
                          </Heading>
                          <Text color="gray.600" fontSize="sm">
                            {service.desc}
                          </Text>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Container>
                </Box>

                {/* Solar CTA */}
                <Box bg="#001f3f" py={16}>
                  <Container maxW="container.lg">
                    <VStack spacing={6} textAlign="center">
                      <Heading size="xl" color="white">
                        Ready to Start Saving?
                      </Heading>
                      <Text color="gray.300" maxW="xl">
                        Get a free, no-obligation quote and see how much you could save with solar.
                      </Text>
                      <Button bg={V3_COLORS.solar.primary} color="white" size="lg" _hover={{ bg: V3_COLORS.solar.hover }} rightIcon={<FaArrowRight />}>
                        Book Your Free Consultation
                      </Button>
                    </VStack>
                  </Container>
                </Box>
              </VStack>
            </TabPanel>

            {/* Finance Home Page Preview */}
            <TabPanel px={0}>
              <VStack spacing={0} align="stretch">
                {/* Finance Hero */}
                <Box
                  position="relative"
                  py={20}
                  overflow="hidden"
                  backgroundImage="url('https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1920&q=80')"
                  backgroundSize="cover"
                  backgroundPosition="center"
                  _before={{
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bg: "rgba(0, 31, 63, 0.88)",
                  }}
                >
                  <Container maxW="container.xl" position="relative" zIndex={1}>
                    <VStack spacing={6} textAlign="center">
                      <Circle size="80px" bg={V3_COLORS.finance.primary} boxShadow={`0 8px 30px rgba(212, 130, 106, 0.4)`}>
                        <Icon as={FaCoins} boxSize={10} color="white" />
                      </Circle>
                      <Badge bg={V3_COLORS.finance.primary} color="white" px={4} py={2} borderRadius="full" fontSize="sm">
                        Finance Division
                      </Badge>
                      <Heading size="3xl" color="white" fontWeight="black">
                        Finance Solutions for Every Stage of Life
                      </Heading>
                      <Text fontSize="xl" color="gray.300" maxW="2xl">
                        From first home buyers to wealth building, we have the expertise to help you achieve your financial goals
                      </Text>
                      <HStack spacing={4} pt={4}>
                        <Button bg={V3_COLORS.finance.primary} color="white" size="lg" _hover={{ bg: V3_COLORS.finance.hover }} rightIcon={<FaArrowRight />}>
                          Get Started
                        </Button>
                        <Button variant="outline" borderColor="white" color="white" size="lg" _hover={{ bg: "whiteAlpha.200" }}>
                          Book Consultation
                        </Button>
                      </HStack>
                    </VStack>
                  </Container>
                </Box>

                {/* Finance Stats */}
                <Box bg="white" py={12} borderBottom="1px solid" borderColor="gray.200">
                  <Container maxW="container.xl">
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
                      {[
                        { value: "$2B+", label: "Loans Settled", sub: "Since 2008" },
                        { value: "40+", label: "Lender Partners", sub: "Best rates guaranteed" },
                        { value: "5,000+", label: "Happy Clients", sub: "5-star reviews" },
                        { value: "48hrs", label: "Pre-Approval", sub: "Average turnaround" },
                      ].map((stat, idx) => (
                        <VStack key={idx}>
                          <Text fontSize="4xl" fontWeight="bold" color={V3_COLORS.finance.primary}>
                            {stat.value}
                          </Text>
                          <Text color="gray.800" fontWeight="600">{stat.label}</Text>
                          <Text color="gray.500" fontSize="sm">{stat.sub}</Text>
                        </VStack>
                      ))}
                    </SimpleGrid>
                  </Container>
                </Box>

                {/* Finance Services */}
                <Box bg="gray.50" py={16}>
                  <Container maxW="container.xl">
                    <VStack spacing={4} mb={12} textAlign="center">
                      <Text color={V3_COLORS.finance.hover} fontWeight="600" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
                        Our Services
                      </Text>
                      <Heading size="xl" color="gray.800">
                        Comprehensive Finance Solutions
                      </Heading>
                      <Text color="gray.600" maxW="2xl">
                        We work for you, not the banks
                      </Text>
                    </VStack>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                      {[
                        { title: "Home Loans", desc: "First homes, upgrades, refinancing & investment loans", features: ["40+ lenders", "Best rates", "Fast approval"] },
                        { title: "Wealth Management", desc: "Strategic planning for long-term financial security", features: ["Tax strategies", "Portfolio review", "Retirement planning"] },
                        { title: "Commercial Finance", desc: "Business loans, equipment finance & cash flow solutions", features: ["Flexible terms", "Quick decisions", "Expert advice"] },
                        { title: "Specialist Finance", desc: "Complex situations, self-employed & unique circumstances", features: ["No-doc loans", "Bad credit", "SMSF lending"] },
                      ].map((service, idx) => (
                        <Box
                          key={idx}
                          p={8}
                          bg="white"
                          borderRadius="2xl"
                          border="1px solid"
                          borderColor="gray.200"
                          boxShadow="sm"
                          _hover={{ borderColor: V3_COLORS.finance.primary, shadow: "lg" }}
                          transition="all 0.3s"
                        >
                          <Heading size="md" color="gray.800" mb={2}>
                            {service.title}
                          </Heading>
                          <Text color="gray.600" fontSize="sm" mb={4}>
                            {service.desc}
                          </Text>
                          <HStack spacing={2} flexWrap="wrap">
                            {service.features.map((feat, fidx) => (
                              <Badge key={fidx} bg={`rgba(212, 130, 106, 0.1)`} color={V3_COLORS.finance.primary} px={2} py={1} borderRadius="md" fontSize="xs">
                                {feat}
                              </Badge>
                            ))}
                          </HStack>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Container>
                </Box>

                {/* Finance Benefits */}
                <Box bg="white" py={16}>
                  <Container maxW="container.xl">
                    <VStack spacing={4} mb={12} textAlign="center">
                      <Heading size="xl" color="gray.800">
                        Why Choose One Finance?
                      </Heading>
                    </VStack>
                    <SimpleGrid columns={{ base: 2, md: 3 }} spacing={6}>
                      {[
                        "Access to 40+ lenders",
                        "Competitive interest rates",
                        "Expert broker advice",
                        "Fast pre-approvals",
                        "No upfront fees",
                        "Ongoing support",
                      ].map((benefit, idx) => (
                        <HStack key={idx} p={4} bg="gray.50" borderRadius="xl">
                          <Circle size="30px" bg={V3_COLORS.finance.primary}>
                            <Icon as={FaCheck} boxSize={3} color="white" />
                          </Circle>
                          <Text color="gray.700" fontWeight="500">{benefit}</Text>
                        </HStack>
                      ))}
                    </SimpleGrid>
                  </Container>
                </Box>

                {/* Finance CTA */}
                <Box bg="#001f3f" py={16}>
                  <Container maxW="container.lg">
                    <VStack spacing={6} textAlign="center">
                      <Heading size="xl" color="white">
                        Ready to Get Started?
                      </Heading>
                      <Text color="gray.300" maxW="xl">
                        Book a free consultation and discover how we can help you achieve your financial goals.
                      </Text>
                      <Button bg={V3_COLORS.finance.primary} color="white" size="lg" _hover={{ bg: V3_COLORS.finance.hover }} rightIcon={<FaArrowRight />}>
                        Book Your Free Consultation
                      </Button>
                    </VStack>
                  </Container>
                </Box>
              </VStack>
            </TabPanel>

            {/* Property Home Page Preview */}
            <TabPanel px={0}>
              <VStack spacing={0} align="stretch">
                {/* Property Hero */}
                <Box
                  position="relative"
                  py={20}
                  overflow="hidden"
                  backgroundImage="url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80')"
                  backgroundSize="cover"
                  backgroundPosition="center"
                  _before={{
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bg: "rgba(0, 31, 63, 0.88)",
                  }}
                >
                  <Container maxW="container.xl" position="relative" zIndex={1}>
                    <VStack spacing={6} textAlign="center">
                      <Circle size="80px" bg={V3_COLORS.property.primary} boxShadow={`0 8px 30px rgba(0, 181, 173, 0.4)`}>
                        <Icon as={FaHome} boxSize={10} color="white" />
                      </Circle>
                      <Badge bg={V3_COLORS.property.primary} color="white" px={4} py={2} borderRadius="full" fontSize="sm">
                        Property Division
                      </Badge>
                      <Heading size="3xl" color="white" fontWeight="black">
                        Build Retirement Wealth Beyond Superannuation
                      </Heading>
                      <Text fontSize="xl" color="gray.300" maxW="2xl">
                        If you're earning $200k+ and concerned your super won't deliver the retirement you deserve, property investment offers a proven path to financial security.
                      </Text>
                      <HStack spacing={4} pt={4}>
                        <Button bg={V3_COLORS.property.primary} color="white" size="lg" _hover={{ bg: V3_COLORS.property.hover }} rightIcon={<FaArrowRight />}>
                          Start Your Journey
                        </Button>
                        <Button variant="outline" borderColor="white" color="white" size="lg" _hover={{ bg: "whiteAlpha.200" }}>
                          Book Consultation
                        </Button>
                      </HStack>
                    </VStack>
                  </Container>
                </Box>

                {/* Property Stats */}
                <Box bg="white" py={12} borderBottom="1px solid" borderColor="gray.200">
                  <Container maxW="container.xl">
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
                      {[
                        { value: "$2.5M+", label: "Portfolio Value Managed" },
                        { value: "18.5%", label: "Avg. Annual Return" },
                        { value: "150+", label: "Properties Advised" },
                        { value: "95%", label: "Client Satisfaction" },
                      ].map((stat, idx) => (
                        <VStack key={idx}>
                          <Text fontSize="4xl" fontWeight="bold" color={V3_COLORS.property.primary}>
                            {stat.value}
                          </Text>
                          <Text color="gray.600">{stat.label}</Text>
                        </VStack>
                      ))}
                    </SimpleGrid>
                  </Container>
                </Box>

                {/* Property Pillars */}
                <Box bg="gray.50" py={16}>
                  <Container maxW="container.xl">
                    <VStack spacing={4} mb={12} textAlign="center">
                      <Text color={V3_COLORS.property.hover} fontWeight="600" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
                        Our Approach
                      </Text>
                      <Heading size="xl" color="gray.800">
                        Three Investment Pillars
                      </Heading>
                    </VStack>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                      {[
                        { title: "Data-Driven Analysis", desc: "Market trends, suburb comparison, and risk assessment to find the right opportunities", icon: FaEye },
                        { title: "Retirement-Focused Planning", desc: "Personalized strategies with milestone tracking and timeline optimization", icon: FaGraduationCap },
                        { title: "Portfolio Diversification", desc: "Risk mitigation through asset class mixing and geographic spread", icon: FaCircle },
                      ].map((pillar, idx) => (
                        <Box
                          key={idx}
                          p={8}
                          bg="white"
                          borderRadius="2xl"
                          border={`2px solid ${V3_COLORS.property.primary}`}
                          textAlign="center"
                          _hover={{ transform: "translateY(-4px)", shadow: "xl" }}
                          transition="all 0.3s"
                        >
                          <Circle size="60px" bg={`rgba(0, 181, 173, 0.1)`} mx="auto" mb={4}>
                            <Icon as={pillar.icon} boxSize={6} color={V3_COLORS.property.primary} />
                          </Circle>
                          <Heading size="md" color="gray.800" mb={2}>
                            {pillar.title}
                          </Heading>
                          <Text color="gray.600" fontSize="sm">
                            {pillar.desc}
                          </Text>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Container>
                </Box>

                {/* Property Benefits */}
                <Box bg="white" py={16}>
                  <Container maxW="container.xl">
                    <VStack spacing={4} mb={12} textAlign="center">
                      <Heading size="xl" color="gray.800">
                        Key Benefits
                      </Heading>
                    </VStack>
                    <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
                      {[
                        { title: "Solar Value-Add", desc: "Boost yields and claim tax deductions with integrated solar" },
                        { title: "Tax Minimisation", desc: "Strategic approaches for $200k+ earners" },
                        { title: "Structured Guidance", desc: "Long-term planning with accountability" },
                        { title: "Retirement Roadmap", desc: "Track portfolio growth alongside super" },
                      ].map((benefit, idx) => (
                        <VStack
                          key={idx}
                          p={6}
                          bg="gray.50"
                          borderRadius="xl"
                          spacing={3}
                          align="start"
                          border="1px solid"
                          borderColor="gray.200"
                        >
                          <Circle size="40px" bg={V3_COLORS.property.primary}>
                            <Icon as={FaCheck} boxSize={4} color="white" />
                          </Circle>
                          <Heading size="sm" color="gray.800">
                            {benefit.title}
                          </Heading>
                          <Text color="gray.600" fontSize="sm">
                            {benefit.desc}
                          </Text>
                        </VStack>
                      ))}
                    </SimpleGrid>
                  </Container>
                </Box>

                {/* Property CTA */}
                <Box bg="#001f3f" py={16}>
                  <Container maxW="container.lg">
                    <VStack spacing={6} textAlign="center">
                      <Heading size="xl" color="white">
                        Ready to Build Your Property Portfolio?
                      </Heading>
                      <Text color="gray.300" maxW="xl">
                        Book a consultation and discover how property investment can secure your retirement.
                      </Text>
                      <Button bg={V3_COLORS.property.primary} color="white" size="lg" _hover={{ bg: V3_COLORS.property.hover }} rightIcon={<FaArrowRight />}>
                        Book Your Free Consultation
                      </Button>
                    </VStack>
                  </Container>
                </Box>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Container>
    </>
  );
};

export default BrandStyleGuideV3;
